import type React from "react";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { ResourceInactivityResponseDto } from "../typings/resource-calendar.types";
import type { ScheduleResourceAutocompleteOption } from "../partial/ScheduleResourceAutocomplete.partial";
import {
  buildResourceCalendarByObjectRequest,
  matchesBookableBookingObject
} from "../utils/resource-booking-schedule.utils";

export type ResourceScheduleFetchStatus = "error" | "loading" | "success" | "idle";

export type SyncResourceModeSchedulesArgs = {
  isResourceMode: boolean;
  calendarFirstDay: string | undefined;
  calendarLastDay: string | undefined;
  bookableBookingObjectsFetching: boolean;
  selectedResource: ScheduleResourceAutocompleteOption | null;
  resourceAutocompleteOptions: ScheduleResourceAutocompleteOption[];
  resourceScheduleByObjectKeyRef: React.MutableRefObject<string>;
  setSelectedResource: React.Dispatch<
    React.SetStateAction<ScheduleResourceAutocompleteOption | null>
  >;
  setResourceSchedules: React.Dispatch<React.SetStateAction<BookingScheduleInterface[]>>;
  setResourceInactivity: React.Dispatch<React.SetStateAction<ResourceInactivityResponseDto | null>>;
  setResourceScheduleStatus: React.Dispatch<React.SetStateAction<ResourceScheduleFetchStatus>>;
  fetchResourceCalendarByBookingObject: (
    params: ReturnType<typeof buildResourceCalendarByObjectRequest>
  ) => Promise<{
    schedules: BookingScheduleInterface[];
    inactivity: ResourceInactivityResponseDto;
  }>;
};

function findSelectedInOptions(
  options: ScheduleResourceAutocompleteOption[],
  selected: ScheduleResourceAutocompleteOption
): ScheduleResourceAutocompleteOption | undefined {
  return options.find(option => matchesBookableBookingObject(option, selected));
}

function buildResourceScheduleRequestKey(
  selected: ScheduleResourceAutocompleteOption,
  calendarFirstDay: string,
  calendarLastDay: string
): string {
  const resourceKindKey =
    selected.bookingType === BookingType.RESOURCE ? selected.resourceKind ?? "" : "";
  const locationKey =
    selected.bookingType === BookingType.RESOURCE ? String(selected.locationInventoryId ?? "") : "";

  return `${selected.bookingInventoryId}\0${selected.bookingType}\0${resourceKindKey}\0${locationKey}\0${calendarFirstDay}\0${calendarLastDay}`;
}

/**
 * Resource calendar: after bookable catalog finishes for the visible range, keep or drop the
 * selected resource, then fetch `by-booking-object` when needed (deduped by ref).
 */
export function syncResourceModeSchedulesFromBookableCatalog(
  args: Readonly<SyncResourceModeSchedulesArgs>
): void {
  const {
    isResourceMode,
    calendarFirstDay,
    calendarLastDay,
    bookableBookingObjectsFetching,
    selectedResource,
    resourceAutocompleteOptions,
    resourceScheduleByObjectKeyRef,
    setSelectedResource,
    setResourceSchedules,
    setResourceInactivity,
    setResourceScheduleStatus,
    fetchResourceCalendarByBookingObject
  } = args;

  if (!isResourceMode) {
    resourceScheduleByObjectKeyRef.current = "";
    return;
  }

  if (!calendarFirstDay || !calendarLastDay) {
    return;
  }

  if (bookableBookingObjectsFetching) {
    if (selectedResource) {
      setResourceScheduleStatus("loading");
    }
    return;
  }

  if (!selectedResource) {
    resourceScheduleByObjectKeyRef.current = "";
    setResourceSchedules([]);
    setResourceInactivity(null);
    setResourceScheduleStatus("idle");
    return;
  }

  if (resourceAutocompleteOptions.length === 0) {
    resourceScheduleByObjectKeyRef.current = "";
    setResourceSchedules([]);
    setResourceInactivity(null);
    setResourceScheduleStatus("idle");
    return;
  }

  const selectedInNewList = findSelectedInOptions(resourceAutocompleteOptions, selectedResource);

  if (!selectedInNewList) {
    resourceScheduleByObjectKeyRef.current = "";
    setSelectedResource(null);
    setResourceSchedules([]);
    setResourceInactivity(null);
    setResourceScheduleStatus("idle");
    return;
  }

  if (selectedInNewList.searchLabel !== selectedResource.searchLabel) {
    setSelectedResource(selectedInNewList);
  }

  const requestKey = buildResourceScheduleRequestKey(
    selectedInNewList,
    calendarFirstDay,
    calendarLastDay
  );
  if (resourceScheduleByObjectKeyRef.current === requestKey) {
    return;
  }
  resourceScheduleByObjectKeyRef.current = requestKey;

  setResourceScheduleStatus("loading");
  fetchResourceCalendarByBookingObject(
    buildResourceCalendarByObjectRequest(selectedInNewList, calendarFirstDay, calendarLastDay)
  )
    .then(({ schedules, inactivity }) => {
      setResourceSchedules(schedules);
      setResourceInactivity(inactivity);
      setResourceScheduleStatus("success");
    })
    .catch(() => {
      setResourceSchedules([]);
      setResourceInactivity(null);
      setResourceScheduleStatus("error");
    });
}
